export type AiCollectionId =
  | "hungru-campaigns"
  | "hungru-flavor-spirit"
  | "hungru-taste-identity"
  | "hungru-future"
  | "havah"
  | "havah-game-board"
  | "animals"
  | "action-figures"
  | "realistic"
  | "creative-with-values"
  | "drawing-like"
  | "studio";

export type LocalizedCopy = { en: string; he: string };

/** Folder-based collections — matches ~/Desktop/shanir_arts_work/ai arts layout. */
export const aiCollectionCopy: Record<AiCollectionId, LocalizedCopy> = {
  "hungru-campaigns": {
    en: "Hungru — Campaigns",
    he: "האנגרו — קמפיינים",
  },
  "hungru-flavor-spirit": {
    en: "Hungru — Flavor Spirit",
    he: "האנגרו — רוח טעם",
  },
  "hungru-taste-identity": {
    en: "Hungru — Taste Identity",
    he: "האנגרו — זהות טעם",
  },
  "hungru-future": {
    en: "Hungru — Future",
    he: "האנגרו — עתיד",
  },
  havah: {
    en: "Havah",
    he: "חווה",
  },
  "havah-game-board": {
    en: "Havah — Game Boards",
    he: "חווה — לוחות משחק",
  },
  animals: {
    en: "Animals",
    he: "חיות",
  },
  "action-figures": {
    en: "Action Figures",
    he: "דמויות אקשן",
  },
  realistic: {
    en: "Realistic",
    he: "ריאליסטי",
  },
  "creative-with-values": {
    en: "Creative with Values",
    he: "יצירה עם ערכים",
  },
  "drawing-like": {
    en: "Drawing Style",
    he: "סגנון ציור",
  },
  studio: {
    en: "Studio",
    he: "סטודיו",
  },
};

/** Gallery filter order — also defines “All” tab artwork order by collection. */
export const aiCollectionFilterIds = [
  "all",
  "creative-with-values",
  "studio",
  "realistic",
  "action-figures",
  "animals",
  "drawing-like",
  "hungru-flavor-spirit",
  "hungru-taste-identity",
  "hungru-future",
  "hungru-campaigns",
  "havah",
  "havah-game-board",
] as const;

export type AiCollectionFilterId = (typeof aiCollectionFilterIds)[number];

/** Collection order for “All” view (excludes הכל). */
export const aiCollectionDisplayOrder = aiCollectionFilterIds.filter(
  (id): id is AiCollectionId => id !== "all",
);

const aiCollectionRank = Object.fromEntries(
  aiCollectionDisplayOrder.map((id, index) => [id, index]),
) as Record<AiCollectionId, number>;

/** Sort works for the “All” AI gallery — same order as filter chips (after הכל). */
export function compareAiArtworksForAllView(
  a: { collection: AiCollectionId },
  b: { collection: AiCollectionId },
): number {
  return aiCollectionRank[a.collection] - aiCollectionRank[b.collection];
}

/** Group works into collection blocks — avoids masonry columns mixing collections. */
export function groupAiArtworksForAllView<T extends { collection: AiCollectionId }>(
  works: T[],
): Array<{ collectionId: AiCollectionId; works: T[] }> {
  const byCollection = new Map<AiCollectionId, T[]>();
  for (const work of works) {
    const list = byCollection.get(work.collection) ?? [];
    list.push(work);
    byCollection.set(work.collection, list);
  }
  return aiCollectionDisplayOrder
    .map((collectionId) => ({
      collectionId,
      works: byCollection.get(collectionId) ?? [],
    }))
    .filter((group) => group.works.length > 0);
}
